import {
  AppNotificationDto,
  AppNotificationGroupViewDto,
  AppNotificationViewDto,
} from '@act/shared/data-transfer-objects';
import {
  AppNotificationDeletedEventPayload,
  AppNotificationEventTypes,
  AppNotificationUpsertedEventPayload,
} from '@act/shared/socket-events';
import {
  PayloadAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { LoadingStatus } from 'redux/types/shared-types';
import { addToEffectsMiddleware } from 'redux/utils/effects-middleware';
import { appNotificationRequests } from '../requests/app-notification.requests';
import { useAppSelector } from '../store-hooks';
import { AppNotificationState } from '../types/appNotificationTypes';
import { createApiThunk } from '../utils/api-thunk';
import { verifyUser } from './actingUserSlice';
import type { WebsocketEventActions } from './websocketSlice';
import { resetRedux } from '../actions/shared-actions';

// THUNKS/ACTIONS ==============================================================
export const getAppNotificationGroupTypes = createApiThunk(
  appNotificationRequests.getAppNotificationGroupTypes
);
export const getAppNotificationsForGuide = createApiThunk(
  appNotificationRequests.getAppNotificationsForGuide
);
export const markAppNotificationsAsSeen = createApiThunk(
  appNotificationRequests.markAppNotificationsAsSeen
);
export const deleteAppNotifications = createApiThunk(
  appNotificationRequests.deleteAppNotifications
);

// INITIAL STATE ===============================================================
const appNotificationsEntityAdapter = createEntityAdapter<AppNotificationDto>();
const initialState: AppNotificationState = {
  status: 'initial',
  appNotifications: appNotificationsEntityAdapter.getInitialState(),
  appNotificationIds: null,
  appNotificationGroups: null,
};

// SLICE =======================================================================
export const appNotificationSlice = createSlice({
  name: 'appNotifications',
  initialState,
  reducers: {
    deleteAppNotifications: (
      state: AppNotificationState,
      action: PayloadAction<AppNotificationDeletedEventPayload>
    ) => {
      appNotificationsEntityAdapter.removeMany(
        state.appNotifications,
        action.payload.appNotificationIds
      );
    },
    upsertAppNotifications: (
      state: AppNotificationState,
      action: PayloadAction<AppNotificationUpsertedEventPayload>
    ) => {
      appNotificationsEntityAdapter.upsertMany(
        state.appNotifications,
        action.payload.appNotificationView.notifications
      );
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getAppNotificationGroupTypes.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        getAppNotificationGroupTypes.fulfilled,
        (state, action: PayloadAction<AppNotificationGroupViewDto>) => {
          state.status = 'success';
          state.appNotificationGroups = action.payload.notificationGroups;
        }
      )
      .addCase(getAppNotificationGroupTypes.rejected, state => {
        state.status = 'failed';
      })
      .addCase(getAppNotificationsForGuide.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        getAppNotificationsForGuide.fulfilled,
        (state, action: PayloadAction<AppNotificationViewDto>) => {
          state.status = 'success';
          appNotificationsEntityAdapter.removeAll(state.appNotifications);
          appNotificationsEntityAdapter.addMany(
            state.appNotifications,
            action.payload.notifications
          );
          state.appNotificationIds = action.payload.notificationIds;
        }
      )
      .addCase(getAppNotificationsForGuide.rejected, state => {
        state.status = 'failed';
      });
  },
});
export const appNotificationActions = appNotificationSlice.actions;

// REDUCER =====================================================================
export const appNotificationReducer = appNotificationSlice.reducer;

// SELECTORS ===================================================================
const selectSliceState = (state): AppNotificationState =>
  (state as { appNotifications: AppNotificationState })?.appNotifications;

const selectAppNotificationSliceStatus = (state): LoadingStatus =>
  (state as { appNotifications: AppNotificationState })?.appNotifications
    ?.status;

const selectAppNotificationGroups = () =>
  createSelector(selectSliceState, state => state.appNotificationGroups);

const selectAppNotificationAdapterState = state =>
  (state as { appNotifications: AppNotificationState })?.appNotifications
    ?.appNotifications;

const selectAppNotifications = createSelector(
  selectSliceState,
  selectAppNotificationAdapterState,
  (state, adapterState) =>
    Object.keys(adapterState.entities)?.reduce((prev, current) => {
      if (state.appNotificationIds[current]) return prev;
      return [...prev, adapterState.entities[current]];
    }, [])
);

const selectAppNotificationById = (id: number) => state =>
  selectAppNotificationAdapterState(state).entities[id];

// EXPORT HELPER ===============================================================
export const appNotificationAppSelectors = {
  useSelectAppNotificationLoadingStatus: () =>
    useAppSelector(selectAppNotificationSliceStatus),
  useSelectAppNotificationGroups: () =>
    useAppSelector(selectAppNotificationGroups()),
  useSelectAppNotifications: (): AppNotificationDto[] =>
    useAppSelector(selectAppNotifications),
  useSelectAppNotificationById: (id: number): AppNotificationDto =>
    useAppSelector(selectAppNotificationById(id)),
};

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: verifyUser.fulfilled,
    effect: async ({ dispatch, state }) => {
      const { appNotificationGroups } = state.appNotifications;
      if (appNotificationGroups === null) {
        dispatch(getAppNotificationGroupTypes({}));
      }
      dispatch(getAppNotificationsForGuide({}));
    },
  });
});
// WEBSOCKET ACTIONS ===========================================================
export const appNotificationWebsocketActions: WebsocketEventActions[] = [
  {
    type: AppNotificationEventTypes.NOTIFICATION_UPSERTED,
    action: appNotificationSlice.actions.upsertAppNotifications,
    channel: 'baseSocket',
  },
  {
    type: AppNotificationEventTypes.NOTIFICATION_DELETED,
    action: appNotificationSlice.actions.deleteAppNotifications,
    channel: 'baseSocket',
  },
];
